import {ChangeDetectorRef, Component, inject, model, output} from '@angular/core';
import {VerifiedDomain} from "@app/shared/_components/verified-domains/verified-domains.interface";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {TranslateService} from '@app/shared/_services/translate.service';
import {ClientValidators} from "@app/shared/_validators/client.validators";
import {VerifiedDomainsService} from "@app/shared/_services/verified-domains.service";
import {DNSRecords} from '@config/config';
import {UserService} from '@app/shared/_services/user.service';
import {getDnsRecords, isDnsRecordVerified} from "@app/shared/_helpers/dns";
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { PrimeTemplate } from 'primeng/api';
import { VerifiedDomainsComponent } from './verified-domains.component';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { NgClass } from '@angular/common';
import { ButtonDirective, Button } from 'primeng/button';
import { TdCopyDirective } from '../../_directives/tdcopy.directive';
import { TranslatePipe } from '../../_pipes/translate.pipe';

@Component({
    selector: 'verified-domains-modal',
    template: `
    @if (visible()) {
      <p-drawer [blockScroll]="true" styleClass="p-drawer-lg" position="right" [(visible)]="visible"
        (onShow)="onShow()"
        (visibleChange)="onVisibleChange($event)">
        <p-toast></p-toast>
        <ng-template pTemplate="header">
          <h3>{{ 'settings_sender_title' | i18n }}</h3>
        </ng-template>
        <div class="p-6">
          @if (maskType === 'list') {
            <div>
              <app-verified-domains (onOpenCreateDomain)="setMaskType('create')" (onOpenVerifyDomain)="openVerification($event)"></app-verified-domains>
            </div>
          }
          @if (maskType === 'create') {
            <div>
              <p>{{'settings_sender_domains_new_intro' | i18n}}</p>
              <div class="flex-grid">
                <div class="col-12 xl:col-8 xl:col-offset-2" [formGroup]="createDomainForm">
                  <div class="thumb-box p-6">
                    <div class="field">
                      <label for="domain">Domain</label>
                      <input id="domain" class="inputfield w-full" formControlName="domain" type="text"
                        pInputText>
                      @if (submitted && createDomainForm.get('domain').errors) {
                        <small
                          class="p-error"><i
                        class="pi pi-exclamation-triangle"></i> {{ 'settings_link_branding_domain_name_invalid' | i18n }}
                      </small>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        @if (maskType === 'domain_created') {
          <div>
            <p-message [closable]="false" severity="success">
              <p [innerHTML]="'settings_sender_domain_added' | i18n : {domain:createDomainForm.get('domain').value}"></p>
            </p-message>
          </div>
        }
        @if (maskType === 'validation') {
          <div>
            @if (!isDomainVerified) {
              <p-table [value]="verificationResults" styleClass="p-datatable-gridlines">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{'settings_sender_verify_record'|i18n}}</th>
                    <th>{{'settings_sender_verify_value'|i18n}}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-result>
                  <tr>
                    <td>{{ result.record }}</td>
                    <td>
                      @if (domainChecking) {
                        <i class="pi pi-spin pi-spinner text-gray-500"></i>
                      } @else {
                        <span [innerHTML]="result.value"></span>
                      }
                    </td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="caption">
                    <div class="text-xl text-center">{{domainName}}</div>
                  </ng-template>
                </p-table>
              } @else {
                <div class="flex-grid text-center">
                  <div class="col-12 xl:col-8 xl:col-offset-2">
                    <div class="thumb-box p-6">
                      <p><i class="pi pi-fw pi-verified text-8xl text-green-500"></i></p>
                      <p class="text-xl font-bold text-green-500">{{domainName}}</p>
                      <p>{{'settings_sender_verified' | i18n}}</p>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
          @if (maskType === 'validation' || maskType === 'domain_created') {
            <div>
              <div class="my-6">
                <h3>{{'settings_sender_dns_title' | i18n }}</h3>
                @if (!isSubaccount) {
                  <div>
                    <p>{{'settings_sender_dns_description' | i18n }}</p>
                    @if (!domainChecking) {
                      <div>
                        <div>
                          <h4 class="mt-4 mb-2" [ngClass]="{'text-green-500' : isSpfVerified }">
                            {{'settings_sender_spf_title' | i18n }}
                            @if (isSpfVerified ) {
                              <i class="pi pi-fw pi-verified text-green-500"></i>
                            }
                          </h4>
                          @if (maskType === 'validation') {
                            <div>
                              <p-message [severity]="isSpfVerified ? 'success' : 'error'">
                                  <div>
                                    @if (!isSpfVerified && spfRecords.length > 0 && spfErrorReason) {
                                      <div class="font-bold mb-4">
                                        {{spfErrorReason | i18n : reasonArgs }}
                                      </div>
                                    }
                                    @if (spfRecords.length > 0) {
                                      <div class="">
                                        @for (record of spfRecords; track record) {
                                          <div class="font-monospace break-word">{{record}}</div>
                                        }
                                      </div>
                                    } @else {
                                      <div class="font-bold">{{'no_records_found' | i18n }}</div>
                                    }
                                  </div>
                              </p-message>
                            </div>
                          }
                          @if (!isSpfVerified || maskType === 'domain_created') {
                            <div>
                              <p>{{'settings_sender_spf_description' | i18n }}</p>
                              <ul>
                                <li>
                                  <p class="mb-2">
                                    <span [innerHTML]="'settings_dns_bar_spf_description_a' | i18n"></span>
                                    <span class="inline-flex items-center gap-2">
                                      <code class="text-pink-600 tocopy" #spfDescPartial><span>{{DNSRecords.SPFPartial}}</span></code>
                                      <button pButton type="button" [label]="'button_copy' | i18n" class="p-button-xs p-button-outlined" icon="pi pi-copy" tdCopy [sourceElement]="spfDescPartial" iconPos="right"></button>
                                    </span>
                                    <span [innerHTML]="'settings_dns_bar_spf_description_b' | i18n"></span> <code class="text-pink-600 tocopy">{{DNSRecords.SPFAfter}}</code>
                                  </p>
                                  <p class="mt-2" [innerHTML]="'settings_dns_bar_spf_description_c' | i18n"></p>
                                </li>
                                <li>
                                  <p><span [innerHTML]="'settings_dns_bar_spf_description_d' | i18n"></span></p>
                                  <div class="flex items-center justify-center gap-2">
                                    <pre #spfDescFull class="tocopy my-0"><span>{{DNSRecords.SPFFull}}</span></pre>
                                    <button pButton type="button" [label]="'button_copy' | i18n" class="p-button-xs p-button-outlined" tdCopy [sourceElement]="spfDescFull" icon="pi pi-copy" iconPos="right"></button>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          }
                        </div>
                        <div>
                          <h4 class="mt-4 mb-2" [ngClass]="{'text-green-500' : isDkimVerified }">
                            {{'settings_sender_dkim_title' | i18n }}
                            @if (isDkimVerified ) {
                              <i class="pi pi-fw pi-verified text-green-500"></i>
                            }
                          </h4>
                          @if (maskType === 'validation') {
                            <div>
                              <p-message [severity]="isDkimVerified ? 'success' : 'error'">
                                  <div>
                                    @if (!isDkimVerified && dkimRecords.length > 0 && dkimErrorReason) {
                                      <div class="font-bold mb-4">
                                        {{dkimErrorReason | i18n: reasonArgs }}
                                      </div>
                                    }
                                    @if (dkimRecords.length > 0) {
                                      <div>
                                        @for (record of dkimRecords; track record) {
                                          <div class="font-monospace break-word">{{record}}</div>
                                        }
                                      </div>
                                    } @else {
                                      <div class="font-bold">{{'no_records_found' | i18n }}</div>
                                    }
                                  </div>
                              </p-message>
                            </div>
                          }
                          @if (!isDkimVerified || maskType === 'domain_created') {
                            <div>
                              <p>{{'settings_sender_dkim_description' | i18n }}</p>
                              <div class="flex-grid">
                                <div class="col-12 lg:col-4">
                                  <strong class="flex items-center gap-2 mb-2">{{'settings_sender_dkim_domain_title' | i18n}}
                                    <button pButton type="button" label="Copy" class="p-button-xs p-button-outlined" icon="pi pi-copy" iconPos="right"  tdCopy [sourceElement]="dkimDomain"></button>
                                  </strong>
                                  <pre #dkimDomain class="tocopy"><span>{{DNSRecords.DKIMDomain}}</span></pre>
                                </div>
                                <div class="col-12 lg:col-8">
                                  <strong class="flex items-center gap-2 mb-2">{{'settings_sender_dkim_value_title' | i18n}}
                                    <button pButton type="button" label="Copy" class="p-button-xs p-button-outlined" icon="pi pi-copy" iconPos="right"  tdCopy [sourceElement]="dkimFull"></button>
                                  </strong>
                                  <pre #dkimFull class="tocopy"><span>{{DNSRecords.DKIMValue}}</span></pre>
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                        <div>
                          <h4 class="mt-4 mb-2" [ngClass]="{'text-green-500' : isDmarcVerified }">
                            {{'settings_sender_dmarc_title' | i18n }}
                            @if (isDmarcVerified ) {
                              <i class="pi pi-fw pi-verified text-green-500"></i>
                            }
                          </h4>
                          @if (maskType === 'validation') {
                            <div>
                              <!-- success -->
                              <p-message [severity]="isDmarcVerified ? 'success' : 'error'">
                                  <div>
                                    @if (!isDmarcVerified && dmarcRecords.length > 0 && dmarcErrorReason) {
                                      <div class="font-bold mb-4">{{dmarcErrorReason | i18n }}</div>
                                    }
                                    @if (dmarcRecords.length > 0) {
                                      <div>
                                        @for (record of dmarcRecords; track record) {
                                          <div class="font-monospace break-word">{{record}}</div>
                                        }
                                      </div>
                                    } @else {
                                      <div class="font-bold">{{'no_records_found' | i18n }}</div>
                                    }
                                  </div>
                              </p-message>
                              @if (isDmarcVerified && dmarcErrorReason) {
                                <p-message severity="warn">
                                    <strong class="pi pi-exclamation-triangle me-1"></strong>
                                    <div class="">{{dmarcErrorReason | i18n: reasonArgs }}</div>
                                </p-message>
                              }
                            </div>
                          }
                          @if (!isDmarcVerified || maskType === 'domain_created') {
                            <div>
                              <p>{{'settings_sender_dmarc_description' | i18n }}</p>
                              <div class="flex-grid">
                                <div class="col-12 lg:col-4">
                                  <strong class="flex items-center gap-2 mb-2">{{'settings_sender_dmarc_domain_title' | i18n}}
                                    <button pButton type="button" label="Copy" class="p-button-xs p-button-outlined" icon="pi pi-copy" iconPos="right"  tdCopy [sourceElement]="dmarcDomain"></button>
                                  </strong>
                                  <pre #dmarcDomain class="tocopy"><span>{{DNSRecords.DMARCDomain}}</span></pre>
                                </div>
                                <div class="col-12 lg:col-8">
                                  <strong class="flex items-center gap-2 mb-2 mb-2">{{'settings_sender_dmarc_value_title' | i18n}}
                                    <button pButton type="button" label="Copy" class="p-button-xs p-button-outlined" icon="pi pi-copy" iconPos="right"  tdCopy [sourceElement]="dmarcFull"></button>
                                  </strong>
                                  <pre #dmarcFull class="tocopy"><span>{{DNSRecords.DMARCValue}}</span></pre>
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    } @else {
                      <p class="text-center mt-12 text-[var(--p-text-muted-color)]"><i class="pi pi-spin pi-spinner" style="font-size: 2.5rem"></i></p>
                      <p class="text-center mt-2 text-[var(--p-text-muted-color)]">{{'settings_sender_loading_domain_data' | i18n }}</p>
                    }
                  </div>
                } @else {
                  <p>{{'settings_sender_dns_ask_admin' | i18n }}</p>
                }
              </div>
            </div>
          }
        </div>
        @if (maskType === 'create') {
          <div class="text-end p-drawer-toolbar-fixed">
            <p-button (onClick)="onVisibleChange(false)" [label]="'button_cancel' | i18n" [outlined]="true"
            styleClass="p-button-outlined p-button-secondary me-2"></p-button>
            <p-button [loading]="addLoading" (onClick)="addVerifiedDomain()"
              [label]="'settings_sender_add_domain'|i18n"
            icon="pi pi-plus" iconPos="right"></p-button>
          </div>
        }
        @if (maskType === 'list') {
          <div class="text-end p-drawer-toolbar-fixed">
            <p-button (onClick)="onVisibleChange(false)" [label]="'button_close' | i18n" [outlined]="true"
            styleClass="p-button-outlined p-button-secondary"></p-button>
          </div>
        }
        @if (maskType === 'validation' || maskType === 'domain_created') {
          <div class="text-end p-drawer-toolbar-fixed">
            @if (!isModal) {
              <p-button (onClick)="onVisibleChange(false)" [label]="'button_close' | i18n" [outlined]="true"
              styleClass="p-button-outlined p-button-secondary"></p-button>
            }
            @if (isModal) {
              <p-button (onClick)="setMaskType('list')" [label]="'settings_sender_verify_ok' | i18n" [outlined]="true"
              styleClass="p-button-outlined p-button-secondary"></p-button>
            }
          </div>
        }
      </p-drawer>
    }
    `,
    imports: [DrawerModule, ToastModule, PrimeTemplate, VerifiedDomainsComponent, FormsModule, ReactiveFormsModule, InputTextModule, MessageModule, TableModule, NgClass, ButtonDirective, TdCopyDirective, Button, TranslatePipe]
})
export class VerifiedDomainsSidebarComponent {
  readonly visible = model<boolean>(false);
  readonly onChangeDomains = output();

  isModal = false;

  maskType : VerifiedDomain.UIMask = 'list';

  submitted = false;

  isSubaccount: boolean = false;

  addLoading: boolean = false;
  domainChecking: boolean = false;
  domainName: string = "";
  isDomainVerified: boolean = false;
  DNSRecords = DNSRecords;
  isDkimVerified: boolean = false;
  isSpfVerified: boolean = false;
  isDmarcVerified: boolean = false;
  spfRecords: string[];
  dkimRecords: string[];
  dmarcRecords: string[];

  private _clientValidators = inject(ClientValidators);
  private verifiedDomainsService = inject(VerifiedDomainsService);
  private _translateService = inject(TranslateService);
  private _userService = inject(UserService);
  private _cdr = inject(ChangeDetectorRef);

  createDomainForm = new FormGroup({
    'domain': new FormControl('', [Validators.required, this._clientValidators.domainSubdomainsValidator])
  });

  verificationResults: VerifiedDomain.VerificationResult[];
  spfErrorReason: string;
  dkimErrorReason: string;
  dmarcErrorReason: string;
  reasonArgs: Record<string, string>;

  onShow() {
    this.isSubaccount = this._userService.getInfoValue('is_subaccount') as boolean;
  }

  onVisibleChange(visible: boolean) {
    this.visible.set(visible);
  }

  setMaskType(_maskType: VerifiedDomain.UIMask) {
    this.maskType = _maskType;
    if (this.maskType === 'create'){
      this.addLoading = false;
      this.submitted = false;
      this.createDomainForm.patchValue({
        'domain': ''
      });
    }
  }

  addVerifiedDomain(){
    this.submitted = true;
    if (this.createDomainForm.valid){
      this.addLoading = true;
      this.verifiedDomainsService.createDomain(
        this.createDomainForm.get('domain').value
      ).subscribe({
        next: _ => {
          this.setMaskType('domain_created');
          this.addLoading = false;
          this.onChangeDomains.emit();
          this._cdr.detectChanges();
        },
        error: error => {
          console.error('Error creating domain', error);
          this.addLoading = false;
          this._cdr.detectChanges();
        }
      });
    }
  }

  openVerification({domainId}: {domainId: number}) {
    this.setMaskType('validation');
    this.domainChecking = true;
    this.verifiedDomainsService.verifyDomain(domainId).subscribe({
      next: data => {
        this.domainName = data.domain;
        this.verificationResults = [
          {
            record: 'SPF',
            verified: data.spf_verified,
            records: data.spf_records,
            value: data.spf_verified ?
              '<i class="pi pi-fw pi-verified text-green-500"></i> ' +  this._translateService.translate(`settings_sender_verify_ok`) :
              '<i class="pi pi-fw pi-exclamation-circle text-red-500"></i> ' +  this._translateService.translate('settings_sender_verify_fail', {'type' : 'SPF'})
          },
          {
            record: 'DKIM',
            verified: data.dkim_verified,
            records: data.dkim_records,
            value: data.dkim_verified ?
              '<i class="pi pi-fw pi-verified text-green-500"></i> ' +  this._translateService.translate(`settings_sender_verify_ok`) :
              '<i class="pi pi-fw pi-exclamation-circle text-red-500"></i> ' +  this._translateService.translate('settings_sender_verify_fail', {'type' : 'DKIM'})
          },
          {
            record: 'DMARC',
            verified: data.dmarc_verified,
            records: data.dmarc_records,
            value: data.dmarc_verified ?
              '<i class="pi pi-fw pi-verified text-green-500"></i> ' +  this._translateService.translate(`settings_sender_verify_ok`) :
              '<i class="pi pi-fw pi-exclamation-circle text-red-500"></i> ' +  this._translateService.translate('settings_sender_verify_fail', {'type' : 'DMARC'})
          }
        ];

        this.spfErrorReason = data.spf_reason;
        this.dkimErrorReason = data.dkim_reason;
        this.dmarcErrorReason = data.dmarc_reason;
        this.reasonArgs = data.reason_args;

        this.isDomainVerified = this.verificationResults.every(record => record.verified);
        this.isDkimVerified = isDnsRecordVerified(
          this.verificationResults,
          'DKIM'
        );

        this.isSpfVerified = isDnsRecordVerified(
          this.verificationResults,
            'SPF'
        );

        this.isDmarcVerified = isDnsRecordVerified(
          this.verificationResults,
          'DMARC'
        );

        this.dkimRecords = getDnsRecords(
          this.verificationResults,
          'DKIM'
        );

        this.spfRecords = getDnsRecords(
          this.verificationResults,
          'SPF'
        );

        this.dmarcRecords = getDnsRecords(
          this.verificationResults,
          'DMARC'
        );

        this.domainChecking = false;
        this.onChangeDomains.emit();
        this._cdr.detectChanges();
      },
      error: error => {
        console.error('Error verifying domain', error);
        this.domainChecking = false;
        this._cdr.detectChanges();
      }
    });
  }
}
